import moment, { Moment } from 'moment';
import { formatISODuration } from 'date-fns';
import { DateRange } from 'src/utils/dateUtils';
import { dutyStatusByEldStatus, DUTY_STATUS, ELD_DRIVER_STATUS } from 'src/constants/ELDConstants';
import { HosLogObject } from '../types';
import { ALASKA_TIME_ZONE } from 'src/utils/dateUtils';
import { GeoLocationDataType, HosGridObject } from '../hosLog/types';
import { isEmpty } from 'lodash';

export const DEFAULT_DATE_VALUE = '';
export const DEFAULT_DATE_LABEL = '--';
export const EMPTY_ERROR = '';
export const DROPDOWN_OPTION_DATE_FORMAT = 'YYYY-MM-DD';
export const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';

export enum FORM_MODE {
  ADD,
  EDIT,
  LOADING,
}

export const confirmButtonTitleByFormMode = {
  [FORM_MODE.ADD]: 'Insert Duty Status',
  [FORM_MODE.EDIT]: 'Confirm Duty Status Change',
  [FORM_MODE.LOADING]: 'Inserting Duty Status...',
};

export const calculateEndTime = (
  startMoment: Moment,
  duration?: moment.DurationInputArg1,
): Date => {
  return startMoment.add(moment.duration(duration, 'milliseconds')).toDate();
};

export const calculateTimestamp = (
  date: string,
  time: string,
  timeZone?: string,
  seconds = 0,
): string => {
  return moment
    .tz(`${date} ${time.replace(ALASKA_TIME_ZONE, '-09:00')}`, `${timeZone ? timeZone : ''}`)
    .seconds(seconds)
    .format();
};

export const validateIntersection = (range1: DateRange, range2: DateRange): boolean => {
  const A = range1.startDate.seconds(0).milliseconds(0);
  const B = range1.endDate.seconds(0).milliseconds(0);
  const C = range2.startDate.seconds(0).milliseconds(0);
  const D = range2.endDate.seconds(0).milliseconds(0);

  if (C.isSameOrAfter(A) && C.isSameOrAfter(B)) return true;

  if (A.isSameOrAfter(C) && A.isSameOrAfter(D)) return true;

  return false;
};

export const normalizeEndTime = (endTimeMoment: Moment, endTime: string): Moment => {
  if (endTime === '00:00') {
    return endTimeMoment.add(24, 'hours');
  }
  return endTimeMoment;
};

export const parseCoordinates = (isAdd: boolean, location: string): [number, number] => {
  const defaultLat = -44.966667;
  const defaultLong = 103.766667;
  if (!isAdd) return [0, 0];

  const coordinates = location.replace(',', '').split(' ');
  const latitude = Number(coordinates[0]) || defaultLat;
  const longitude = Number(coordinates[1]) || defaultLong;
  return [latitude, longitude];
};

export const hoursToMiliseconds = (hours: number): number => {
  return hours * 60 * 60 * 1000;
};

const minutesToMiliseconds = (minutes: number): number => {
  return minutes * 60 * 1000;
};

export const getIsoDurationParamsByPeriod = (
  startValue: string,
  endValue: string,
): [number, number, number] => {
  const startSplit = startValue.split(':');
  const endSplit = endValue.split(':');
  const editedStartHours = parseFloat(startSplit[0]);
  const editedStartMins = parseFloat(startSplit[1]);
  const startDuration =
    hoursToMiliseconds(editedStartHours) + minutesToMiliseconds(editedStartMins);
  const editedEndHours = parseFloat(endSplit[0]);
  const editedEndMins = parseFloat(endSplit[1]);
  const endDuration = hoursToMiliseconds(editedEndHours) + minutesToMiliseconds(editedEndMins);

  const endStartDuration =
    (endDuration === 0 ? hoursToMiliseconds(24) : endDuration) - startDuration;

  const hours =
    moment.duration(endStartDuration).days() > 0 ? 24 : moment.duration(endStartDuration).hours();
  return [
    hours,
    moment.duration(endStartDuration).minutes(),
    moment.duration(endStartDuration).seconds(),
  ];
};

export const getIsoDurationParamsByDuration = (
  duration: number | moment.Duration,
): [number, number, number, number] => {
  return [
    moment.duration(duration).days(),
    moment.duration(duration).hours(),
    moment.duration(duration).minutes(),
    moment.duration(duration).seconds(),
  ];
};

export const createPeriodPayload = (
  eventLogObject: HosLogObject,
  timestamp: string,
  duration: string,
  location: string,
  geoLocationData: GeoLocationDataType | null | undefined,
  notes: string,
  dutyStatusName = DUTY_STATUS.OFF_DUTY,
  odometer: number | null,
  editedOdometer: number | null,
): any => ({
  employeeId: eventLogObject.employeeId,
  logDate: eventLogObject.logDate,
  statusPayload: {
    timestamp,
    location: location ? location : 'EMPTY',
    comment: notes ? notes : 'EMPTY',
    geoLocationData,
    duration,
    dutyStatusName,
    odometer: odometer || null,
    editedOdometer: editedOdometer || null,
  },
});

export const findContainingPeriod = (
  eventLogObject: HosLogObject,
  time: any,
  zone: string,
): HosGridObject | undefined => {
  const eventMoment = time;

  for (let i = 0; i < eventLogObject.hosGridObject.length; i++) {
    const currentEvent = eventLogObject.hosGridObject[i];
    const currentEvenStart = moment(currentEvent.timeISO);
    const currentEventEnd = moment(currentEvent.timeISO).add(currentEvent.duration, 'milliseconds');
    if (currentEvenStart.isBefore(eventMoment) && currentEventEnd.isAfter(eventMoment)) {
      return { ...currentEvent };
    }
  }

  return undefined;
};

export const findNextEventByEvent = (
  eventLogObject: HosLogObject,
  event: HosGridObject,
): HosGridObject | undefined => {
  const eventIndex = eventLogObject.hosGridObject.findIndex(
    item => (item.timeISO || item.time) === (event.timeISO || event.time),
  );

  if (eventIndex === eventLogObject.hosGridObject.length - 1) return undefined;

  return eventLogObject.hosGridObject[eventIndex + 1];
};

export const createNextPeriodPayload = (
  eventLogObject: HosLogObject,
  selectedStartTime: any,
  selectedEndTime: any,
  timeZone: string,
  formMode: FORM_MODE,
): any => {
  let payload;
  const startTimeContainingEvent = findContainingPeriod(
    eventLogObject,
    selectedStartTime,
    timeZone,
  );
  const endTimeContainingEvent = findContainingPeriod(eventLogObject, selectedEndTime, timeZone);
  if (endTimeContainingEvent) {
    const nextTimestamp = selectedEndTime.format();
    const nextAfterNextEvent = findNextEventByEvent(eventLogObject, endTimeContainingEvent);
    let nextOdometer = endTimeContainingEvent.odometer;
    let nextEditedOdometer = endTimeContainingEvent.editedOdometer;
    let nextDuration = 'PT0H0M0S';

    if (nextAfterNextEvent && formMode === FORM_MODE.ADD) {
      const duration = moment.duration(
        moment(nextAfterNextEvent.timeISO).diff(selectedEndTime, 'minutes'),
      );
      const [days, hours, minutes, seconds] = getIsoDurationParamsByDuration(duration);

      nextDuration = formatISODuration({
        days,
        hours,
        minutes,
        seconds,
      }).replace('0Y0M', '');
    }

    if (
      formMode === FORM_MODE.ADD &&
      startTimeContainingEvent &&
      (startTimeContainingEvent.timeISO || startTimeContainingEvent.time) ==
        (endTimeContainingEvent.timeISO || endTimeContainingEvent.time)
    ) {
      nextOdometer = null;
      nextEditedOdometer = null;
    }

    payload = createPeriodPayload(
      eventLogObject,
      nextTimestamp,
      nextDuration,
      endTimeContainingEvent?.location,
      endTimeContainingEvent?.geoLocationData || null,
      endTimeContainingEvent?.notes,
      dutyStatusByEldStatus[endTimeContainingEvent?.status as ELD_DRIVER_STATUS],
      nextOdometer,
      nextEditedOdometer,
    );
  }

  return payload;
};

export const round = (num: number | null, digits = 1): any => {
  if (num == null) {
    return null;
  }
  const multiplier = Math.pow(10, digits);
  return (Math.round(num * multiplier) / multiplier).toFixed(1);
};

export const generateCompliantLocationString = (geoLocationData: GeoLocationDataType | null) => {
  const { distance_mi, direction, jurisdiction, city, lat, lng } = geoLocationData || {};
  // Grab our properties to make the location string.  If we don't have
  // any of the properties for the location string, default to `${lat}, ${lng}` as
  // specified in the ELD specs.
  if (distance_mi && direction && jurisdiction && city) {
    return `${distance_mi.toFixed(1)} mi ${direction} ${city}, ${jurisdiction}`;
  } else if (lat && lng) {
    return `${lat}, ${lng}`;
  } else {
    return null;
  }
};

export const supportTimeISO = (hosGridObject: any) => {
  return hosGridObject.some((event: any) => Boolean(event.timeISO));
};

export const normalizeHosLog = (hosLog: any) => {
  if (isEmpty(hosLog)) return hosLog;
  let resultLog = {
    ...hosLog,
  };
  const logStartTimeISO = moment.tz(`${hosLog.logDate} 00:00`, hosLog.timeZone);
  const logEndTimeISO = moment.tz(`${hosLog.logDate} 00:00`, hosLog.timeZone).add(1, 'day');
  const logLengthHours = logEndTimeISO.diff(logStartTimeISO, 'hours');
  if (!hosLog.logStartTimeISO || !hosLog.logEndTimeISO) {
    resultLog = {
      ...hosLog,
      logDayStartTime: 0,
      logStartTimeISO: logStartTimeISO.toISOString(),
      logEndTimeISO: logEndTimeISO.toISOString(),
      logLengthHours,
    };
  }
  if (!supportTimeISO(hosLog.hosGridObject)) {
    const updatedHosGridObj = hosLog.hosGridObject?.map((event: any) => {
      const timeValue = event.time.substring(0, 5);
      const timeISO = moment.tz(`${hosLog.logDate} ${timeValue}`, hosLog.timeZone).toISOString();
      return {
        ...event,
        timeISO,
      };
    });
    resultLog = {
      ...resultLog,
      hosGridObject: updatedHosGridObj,
    };
  }
  return resultLog;
};
